import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Monitor, Tablet, Smartphone, Undo2, Redo2, Save, Rocket, Eye, History,
  EyeOff, Copy, Trash2, ArrowUp, ArrowDown, GripVertical, Upload, PanelTop, Sparkles,
  Type, AlignLeft, Image as ImageIcon, Video, MousePointerClick, Award, LayoutGrid,
  Play, ShoppingBag, Tags, Search, ShoppingCart, IndianRupee, Quote, Star, Users,
  BarChart3, Timer, Zap, HelpCircle, Mail, MapPin, Share2, Bell, Megaphone, Code, Minus,
  Plus, ChevronDown, ChevronUp, Youtube, Trash,
} from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fileUrl, fmtDateTime } from "../lib/api";
import EditorCanvas from "../editor/EditorCanvas";
import { GROUPS, META, FIELDS, makeSection, defaultDoc, uid, makeServiceCard, makePricingCard, makeYoutubeVideo } from "../editor/elements";
import { validateDoc, waLink, isWa, BUTTON_TYPES, INTERNAL_PAGES, isEcommerceSite } from "../lib/cta";
import { Input } from "../components/ui/input";

const ICONS = {
  header: PanelTop, hero: Sparkles, footer: PanelTop, spacer: Minus, divider: Minus,
  heading: Type, text: AlignLeft, image: ImageIcon, video: Video, button: MousePointerClick,
  logo: Award, gallery: LayoutGrid, slider: Play, html: Code,
  products: ShoppingBag, categories: Tags, search: Search, cart: ShoppingCart, pricing: IndianRupee,
  testimonials: Quote, reviews: Star, team: Users, stats: BarChart3, countdown: Timer,
  services: Zap, faq: HelpCircle, contact: Mail, map: MapPin, social: Share2, newsletter: Bell, cta: Megaphone,
  youtube: Youtube,
};

const HEAD_FONTS = ["Playfair Display", "Fraunces", "DM Serif Display", "Sora", "Outfit", "Libre Franklin", "Cabinet Grotesk", "Space Grotesk"];
const BODY_FONTS = ["Manrope", "Lora", "Outfit", "Space Grotesk", "Libre Franklin"];
const COLOR_KEYS = [["primary", "Primary / buttons"], ["secondary", "Secondary surface"], ["accent", "Accent"], ["bg", "Page background"], ["text", "Text"]];

/* Materialize category-adapted default Services+Pricing sections into a brand-new editor doc. */
function withContentDefaults(doc, cd) {
  if (!cd || !cd.services) return doc;
  const secs = (doc?.sections || []).filter((s) => s.type !== "services" && s.type !== "pricing");
  const idx = secs.findIndex((s) => s.type === "contact" || s.type === "footer");
  const at = idx === -1 ? secs.length : idx;
  secs.splice(at, 0, cd.services, ...(cd.pricing ? [cd.pricing] : []));
  return { ...doc, sections: secs };
}

/* ===== Repeater (card list) editor for services / pricing / youtube sections ===== */
const CARD_SCHEMAS = {
  service: {
    addLabel: "Add Service", itemName: (c) => c.name || "Untitled service", make: makeServiceCard, cta: true, serviceAction: true,
    fields: [
      { k: "name", t: "text", l: "Service name" },
      { k: "image", t: "image", l: "Service image" },
      { k: "description", t: "textarea", l: "Short description" },
      { k: "includes", t: "list", l: "What's included (one per line)" },
      { k: "price", t: "text", l: "Price (e.g. ₹9,999 or Starting from ₹9,999)" },
      { k: "offerPrice", t: "text", l: "Offer / discount price (optional)" },
      { k: "duration", t: "text", l: "Duration / timeline (optional)" },
      { k: "details", t: "textarea", l: "Details page — long description (optional)" },
      { k: "faq", t: "pairs", l: "Details page — FAQ (Question|Answer per line, optional)" },
    ],
  },
  pricing: {
    addLabel: "Add Package", itemName: (c) => c.name || "Untitled package", make: makePricingCard, cta: true, serviceAction: false,
    fields: [
      { k: "name", t: "text", l: "Package name" },
      { k: "subtitle", t: "text", l: "Subtitle (optional)" },
      { k: "icon", t: "image", l: "Icon / image (optional)" },
      { k: "price", t: "text", l: "Price (e.g. 9,999)" },
      { k: "offerPrice", t: "text", l: "Offer price (optional)" },
      { k: "currency", t: "select", l: "Currency", opts: ["₹", "$", "€", "£"] },
      { k: "billingPeriod", t: "text", l: "Billing period (e.g. month, one-time)" },
      { k: "description", t: "textarea", l: "Description (optional)" },
      { k: "features", t: "list", l: "Features (one per line)" },
      { k: "highlighted", t: "toggle", l: "Highlight as recommended" },
    ],
  },
  youtube: {
    addLabel: "Add Video", itemName: (v) => v.title || "Untitled video", make: makeYoutubeVideo, cta: false, serviceAction: false,
    fields: [
      { k: "title", t: "text", l: "Video title" },
      { k: "youtubeUrl", t: "text", l: "YouTube URL" },
      { k: "description", t: "textarea", l: "Short description (optional)" },
      { k: "thumbnail", t: "image", l: "Custom thumbnail (optional — auto from YouTube)" },
      { k: "order", t: "text", l: "Display order (number, optional)" },
      { k: "active", t: "toggle", l: "Visible on website" },
    ],
  },
};

function CtaFields({ c, upd, serviceAction }) {
  const bt = c.buttonType || "none";
  const types = BUTTON_TYPES.filter(([v]) => v !== "service" || serviceAction);
  return (
    <div className="rounded-xl bg-muted/50 border border-border p-3 space-y-3">
      <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground">Call-to-action button</p>
      <div>
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Button text</p>
        <Input data-testid="cta-text" value={c.buttonText ?? ""} onChange={(e) => upd("buttonText", e.target.value)} className="h-9" placeholder="e.g. Book Now, Get Quote, Buy Now" />
      </div>
      <div>
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Button action</p>
        <select data-testid="cta-type" value={bt} onChange={(e) => upd("buttonType", e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
          {types.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </div>
      {bt === "whatsapp" && (
        <>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">WhatsApp number (with country code)</p>
            <Input data-testid="cta-wa-number" value={c.whatsappNumber ?? ""} onChange={(e) => upd("whatsappNumber", e.target.value)} className="h-9" placeholder="919876543210" />
            {c.whatsappNumber && !isWa(c.whatsappNumber) && <p className="text-[10px] text-red-500 mt-1 font-bold">Invalid number — digits only with country code (8–15 digits)</p>}
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Pre-filled message (optional)</p>
            <textarea data-testid="cta-wa-message" rows={2} value={c.whatsappMessage ?? ""} onChange={(e) => upd("whatsappMessage", e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" placeholder="Hi, I am interested in…" />
            {isWa(c.whatsappNumber) && <p className="text-[10px] text-emerald-600 mt-1 break-all">{waLink(c.whatsappNumber, c.whatsappMessage)}</p>}
          </div>
        </>
      )}
      {(bt === "url" || bt === "payment") && (
        <div>
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">{bt === "payment" ? "Payment link" : "External URL"}</p>
          <Input data-testid="cta-url" value={c.buttonUrl ?? ""} onChange={(e) => upd("buttonUrl", e.target.value)} className="h-9" placeholder="https://…" />
        </div>
      )}
      {bt === "page" && (
        <div>
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Website page</p>
          <select data-testid="cta-page" value={c.page || "home"} onChange={(e) => upd("page", e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
            {INTERNAL_PAGES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
      )}
      {bt === "service" && <p className="text-[11px] text-muted-foreground leading-relaxed">Opens this service's auto-generated details page (image, full description, includes, price, FAQ).</p>}
    </div>
  );
}

function CardListEditor({ sec, of, listKey, setProp, upload }) {
  const schema = CARD_SCHEMAS[of];
  const items = Array.isArray(sec.props[listKey]) ? sec.props[listKey] : [];
  const [openId, setOpenId] = useState(null);
  const write = (next) => setProp(sec.id, listKey, next);
  const upd = (cid, k, v) => write(items.map((c) => (c.id === cid ? { ...c, [k]: v } : c)));
  const add = () => { const c = schema.make(); write([...items, c]); setOpenId(c.id); };
  const del = (cid) => write(items.filter((c) => c.id !== cid));
  const dup = (cid) => {
    const i = items.findIndex((c) => c.id === cid);
    const cp = { ...JSON.parse(JSON.stringify(items[i])), id: uid() };
    write([...items.slice(0, i + 1), cp, ...items.slice(i + 1)]);
  };
  const mv = (cid, d) => {
    const i = items.findIndex((c) => c.id === cid); const j = i + d;
    if (j < 0 || j >= items.length) return;
    const a = [...items]; [a[i], a[j]] = [a[j], a[i]]; write(a);
  };
  return (
    <div className="space-y-2" data-testid={`cards-${of}`}>
      {items.map((c, idx) => (
        <div key={c.id} className="border border-border rounded-xl overflow-hidden bg-background" data-testid={`card-item-${c.id}`}>
          <div className="flex items-center gap-0.5 px-2 py-1.5 bg-muted/40">
            <button type="button" className="flex-1 text-left text-xs font-bold truncate px-1.5 py-1 flex items-center gap-1.5" onClick={() => setOpenId(openId === c.id ? null : c.id)} data-testid={`card-toggle-${c.id}`}>
              {openId === c.id ? <ChevronUp className="h-3 w-3 shrink-0" /> : <ChevronDown className="h-3 w-3 shrink-0" />}
              <span className="truncate">{idx + 1}. {schema.itemName(c)}</span>
            </button>
            <button type="button" onClick={() => mv(c.id, -1)} data-testid={`card-up-${c.id}`} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronUp className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => mv(c.id, 1)} data-testid={`card-down-${c.id}`} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center"><ChevronDown className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => dup(c.id)} data-testid={`card-dup-${c.id}`} className="h-7 w-7 rounded-md hover:bg-muted flex items-center justify-center"><Copy className="h-3.5 w-3.5" /></button>
            <button type="button" onClick={() => del(c.id)} data-testid={`card-del-${c.id}`} className="h-7 w-7 rounded-md hover:bg-red-500 hover:text-white flex items-center justify-center"><Trash className="h-3.5 w-3.5" /></button>
          </div>
          {openId === c.id && (
            <div className="p-3 space-y-3 border-t border-border">
              {schema.fields.map((f) => (
                <div key={f.k}>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">{f.l}</p>
                  {f.t === "text" && <Input data-testid={`card-field-${f.k}`} value={c[f.k] ?? ""} onChange={(e) => upd(c.id, f.k, e.target.value)} className="h-9" />}
                  {f.t === "textarea" && <textarea data-testid={`card-field-${f.k}`} rows={3} value={c[f.k] ?? ""} onChange={(e) => upd(c.id, f.k, e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" />}
                  {f.t === "list" && <textarea data-testid={`card-field-${f.k}`} rows={4} value={(Array.isArray(c[f.k]) ? c[f.k] : []).join("\n")} onChange={(e) => upd(c.id, f.k, e.target.value.split("\n"))} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" />}
                  {f.t === "pairs" && <textarea data-testid={`card-field-${f.k}`} rows={3} value={c[f.k] ?? ""} onChange={(e) => upd(c.id, f.k, e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" />}
                  {f.t === "select" && (
                    <select data-testid={`card-field-${f.k}`} value={c[f.k] ?? ""} onChange={(e) => upd(c.id, f.k, e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm">
                      {f.opts.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
                    </select>
                  )}
                  {f.t === "toggle" && (
                    <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                      <input type="checkbox" data-testid={`card-field-${f.k}`} checked={!!c[f.k]} onChange={(e) => upd(c.id, f.k, e.target.checked)} className="h-4 w-4 accent-[var(--p2)]" />
                      {c[f.k] ? "On" : "Off"}
                    </label>
                  )}
                  {f.t === "image" && (
                    <div className="space-y-2">
                      {c[f.k] && <img src={fileUrl(c[f.k])} alt="" className="w-full h-20 object-cover rounded-lg border border-border" />}
                      <div className="flex gap-2">
                        <Input data-testid={`card-field-${f.k}`} value={c[f.k] ?? ""} onChange={(e) => upd(c.id, f.k, e.target.value)} placeholder="Image URL" className="h-9 text-xs" />
                        <label className="h-9 px-3 rounded-md border border-border text-xs font-bold flex items-center gap-1 cursor-pointer hover:bg-muted shrink-0">
                          <Upload className="h-3 w-3" /> Upload
                          <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) upload(file, (url) => upd(c.id, f.k, url));
                            e.target.value = "";
                          }} />
                        </label>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {schema.cta && <CtaFields c={c} upd={(k, v) => upd(c.id, k, v)} serviceAction={schema.serviceAction} />}
            </div>
          )}
        </div>
      ))}
      <button type="button" onClick={add} data-testid={`card-add-${of}`}
        className="w-full h-10 rounded-xl border-2 border-dashed border-border text-xs font-extrabold hover:border-foreground/40 hover:bg-muted/40 transition-colors flex items-center justify-center gap-1.5">
        <Plus className="h-3.5 w-3.5" /> {schema.addLabel}
      </button>
    </div>
  );
}

export default function VisualEditor() {
  const { wid: paramWid } = useParams();
  const navigate = useNavigate();
  const [wid, setWid] = useState(paramWid || null);
  const [payload, setPayload] = useState(null);
  const [doc, setDoc] = useState(null);
  const [products, setProducts] = useState([]);
  const [sel, setSel] = useState(null);
  const [device, setDevice] = useState("desktop");
  const [past, setPast] = useState([]);
  const [future, setFuture] = useState([]);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const [showVersions, setShowVersions] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dragId = useRef(null);

  useEffect(() => {
    if (!paramWid) api.get("/store-admin/my-website").then((r) => setWid(r.data.id)).catch((e) => setErr(errMsg(e)));
  }, [paramWid]);

  useEffect(() => {
    if (!wid) return;
    api.get(`/editor/${wid}`).then((r) => {
      setPayload(r.data);
      if (r.data.theme?.re) { window.location.replace("/store/re-editor"); return; }  // RE sites use the dedicated RE Site Editor
      const d = r.data.draft || r.data.published || withContentDefaults(defaultDoc(r.data.theme || {}, r.data.website || {}), r.data.content_defaults);
      d.sections = Array.isArray(d.sections) ? d.sections : [];
      d.design = d.design || {};
      setDoc(d);
      api.get(`/public/store/${r.data.website.slug}/products`).then((r2) => setProducts(r2.data)).catch(() => {});
    }).catch((e) => setErr(errMsg(e)));
  }, [wid]);

  const commit = (next) => { setPast((p) => [...p.slice(-29), doc]); setFuture([]); setDoc(next); setDirty(true); };
  const undo = () => { if (!past.length) return; setFuture((f) => [doc, ...f]); setDoc(past[past.length - 1]); setPast((p) => p.slice(0, -1)); setDirty(true); };
  const redo = () => { if (!future.length) return; setPast((p) => [...p, doc]); setDoc(future[0]); setFuture((f) => f.slice(1)); setDirty(true); };

  const sec = doc?.sections.find((s) => s.id === sel);
  const mutate = (id, fn) => commit({ ...doc, sections: doc.sections.map((s) => (s.id === id ? fn(s) : s)) });
  const setProp = (id, k, v) => mutate(id, (s) => ({ ...s, props: { ...s.props, [k]: v } }));
  const setStyle = (id, k, v) => mutate(id, (s) => ({ ...s, style: { ...s.style, [k]: v } }));
  const addAt = (type, idx) => { const s = makeSection(type); commit({ ...doc, sections: [...doc.sections.slice(0, idx), s, ...doc.sections.slice(idx)] }); setSel(s.id); };
  const move = (id, d) => {
    const i = doc.sections.findIndex((s) => s.id === id); const j = i + d;
    if (j < 0 || j >= doc.sections.length) return;
    const arr = [...doc.sections];[arr[i], arr[j]] = [arr[j], arr[i]];
    commit({ ...doc, sections: arr });
  };
  const duplicate = (id) => {
    const i = doc.sections.findIndex((s) => s.id === id);
    const cp = JSON.parse(JSON.stringify(doc.sections[i]));
    cp.id = uid();
    for (const k of ["cards", "packages", "videos"]) if (Array.isArray(cp.props?.[k])) cp.props[k].forEach((c) => { c.id = uid(); });
    commit({ ...doc, sections: [...doc.sections.slice(0, i + 1), cp, ...doc.sections.slice(i + 1)] });
  };
  const remove = (id) => { commit({ ...doc, sections: doc.sections.filter((s) => s.id !== id) }); if (sel === id) setSel(null); };
  const moveTo = (drag, target) => {
    if (!drag || drag === target) return;
    const item = doc.sections.find((s) => s.id === drag);
    const arr = doc.sections.filter((s) => s.id !== drag);
    arr.splice(arr.findIndex((s) => s.id === target), 0, item);
    commit({ ...doc, sections: arr });
  };
  const upload = async (file, cb) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/uploads", fd); cb(data.url); toast.success("Image uploaded"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const saveDraft = async () => {
    setBusy("save");
    try { await api.put(`/editor/${wid}/draft`, doc); setDirty(false); toast.success("Draft saved"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const publish = async () => {
    const errs = validateDoc(doc);
    if (errs.length) {
      setSel(errs[0].sectionId);
      toast.error(`Fix ${errs.length} issue(s) before publishing`, { description: errs.slice(0, 3).map((e) => e.msg).join(" · "), duration: 8000 });
      return;
    }
    setBusy("pub");
    try {
      await api.put(`/editor/${wid}/draft`, doc);
      await api.post(`/editor/${wid}/publish`);
      const r2 = await api.get(`/editor/${wid}`);
      setPayload((p) => ({ ...p, published: doc, versions: r2.data.versions }));
      setDirty(false);
      toast.success("Published — changes are live");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const restore = async (vid) => {
    try {
      const r = await api.post(`/editor/${wid}/restore/${vid}`);
      commit(r.data.draft); setShowVersions(false);
      toast.success("Version restored into draft — Publish to make it live");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const setDesign = (k, v) => commit({ ...doc, design: { ...doc.design, [k]: v } });
  const setColor = (k, v) => setDesign("colors", { ...(doc.design.colors || {}), [k]: v });

  if (err) return <div className="min-h-[60vh] flex items-center justify-center"><p data-testid="editor-error" className="text-muted-foreground">{err}</p></div>;
  if (!doc || !payload) return <div className="min-h-[60vh] flex items-center justify-center"><div className="h-10 w-10 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;

  const w = payload.website;
  const canvasWidth = device === "desktop" ? "100%" : device === "tablet" ? 820 : 400;

  return (
    <div className="-m-4 sm:-m-8 lg:-m-12 h-[calc(100vh-64px)] flex flex-col bg-zinc-100 dark:bg-zinc-950" data-testid="visual-editor">
      {/* ===== TOP BAR ===== */}
      <div className="h-14 shrink-0 bg-card border-b border-border flex items-center gap-2 px-3 sm:px-4">
        <button data-testid="editor-back" onClick={() => navigate(-1)} className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center transition-colors"><ArrowLeft className="h-4 w-4" /></button>
        <div className="min-w-0">
          <p className="text-sm font-extrabold truncate">{w.name}</p>
          <p className="text-[10px] text-muted-foreground">{payload.published ? (dirty ? "Published · unsaved changes" : "Published") : "Draft"}</p>
        </div>
        <div className="hidden md:flex items-center gap-1 border border-border rounded-full p-1 mx-2">
          {[["desktop", Monitor], ["tablet", Tablet], ["mobile", Smartphone]].map(([d, Icon]) => (
            <button key={d} data-testid={`editor-device-${d}`} onClick={() => setDevice(d)}
              className={`h-7 w-7 rounded-full flex items-center justify-center transition-colors ${device === d ? "gradient-bg text-white" : "text-muted-foreground"}`}>
              <Icon className="h-3.5 w-3.5" />
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1">
          <button data-testid="editor-undo" onClick={undo} disabled={!past.length} className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center disabled:opacity-30 transition-colors"><Undo2 className="h-4 w-4" /></button>
          <button data-testid="editor-redo" onClick={redo} disabled={!future.length} className="h-9 w-9 rounded-full hover:bg-muted flex items-center justify-center disabled:opacity-30 transition-colors"><Redo2 className="h-4 w-4" /></button>
        </div>
        <div className="relative">
          <button data-testid="editor-versions" onClick={() => setShowVersions(!showVersions)} className="h-9 px-3 rounded-full hover:bg-muted flex items-center gap-1.5 text-xs font-bold transition-colors">
            <History className="h-3.5 w-3.5" /> Versions ({payload.versions?.length || 0})
          </button>
          {showVersions && (
            <div className="absolute right-0 top-11 z-50 w-80 bg-card border border-border rounded-xl shadow-2xl p-2 max-h-80 overflow-y-auto" data-testid="versions-list">
              {!(payload.versions || []).length && <p className="text-xs text-muted-foreground p-4 text-center">No published versions yet</p>}
              {[...(payload.versions || [])].reverse().map((v) => (
                <button key={v.id} data-testid={`version-${v.id}`} onClick={() => restore(v.id)}
                  className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-muted transition-colors">
                  <p className="text-xs font-bold">{v.note}</p>
                  <p className="text-[10px] text-muted-foreground">{fmtDateTime(v.saved_at)}</p>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button data-testid="editor-preview" onClick={() => window.open(`/site/${w.slug}`, "_blank")}
            className="h-9 px-3.5 rounded-full border border-border text-xs font-bold flex items-center gap-1.5 hover:bg-muted transition-colors">
            <Eye className="h-3.5 w-3.5" /> <span className="hidden sm:inline">Preview</span>
          </button>
          <button data-testid="editor-save" onClick={saveDraft} disabled={busy === "save"}
            className="h-9 px-4 rounded-full border border-border text-xs font-bold flex items-center gap-1.5 hover:bg-muted transition-colors disabled:opacity-50">
            <Save className="h-3.5 w-3.5" /> {busy === "save" ? "Saving…" : "Save Draft"}
          </button>
          <button data-testid="editor-publish" onClick={publish} disabled={busy === "pub"}
            className="h-9 px-5 rounded-full gradient-bg text-white text-xs font-extrabold flex items-center gap-1.5 glow-sm active:scale-95 transition-transform disabled:opacity-50">
            <Rocket className="h-3.5 w-3.5" /> {busy === "pub" ? "Publishing…" : "Publish"}
          </button>
        </div>
      </div>

      {/* ===== BODY: palette / canvas / settings ===== */}
      <div className="flex-1 grid lg:grid-cols-[230px_1fr_300px] min-h-0">
        {/* LEFT: elements */}
        <div className="hidden lg:block bg-card border-r border-border overflow-y-auto p-3" data-testid="editor-palette">
          {GROUPS.map((g) => (
            <div key={g} className="mb-4">
              <p className="overline-label px-1 mb-2">{g}</p>
              <div className="grid grid-cols-2 gap-1.5">
                {Object.entries(META).filter(([, m]) => m.group === g)
                  .filter(([t]) => !(isEcommerceSite(payload?.website || {}, payload?.theme || {}) && (t === "services" || t === "pricing")))
                  .map(([type, m]) => {
                  const Icon = ICONS[type] || Zap;
                  return (
                    <button key={type} data-testid={`palette-${type}`} draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/zylo-type", type)}
                      onClick={() => addAt(type, doc.sections.length)}
                      className="flex flex-col items-center gap-1.5 px-2 py-3 rounded-xl border border-border hover:border-foreground/40 hover:shadow-sm transition-all cursor-grab active:cursor-grabbing bg-background">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span className="text-[10px] font-bold text-center leading-tight">{m.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* CENTER: canvas */}
        <div className="overflow-y-auto p-4 sm:p-6" onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const t = e.dataTransfer.getData("text/zylo-type"); if (t) addAt(t, doc.sections.length); dragId.current = null; }}>
          <div className="mx-auto bg-white shadow-2xl transition-all duration-300 min-h-[70vh] rounded-xl overflow-hidden" style={{ maxWidth: canvasWidth }} data-testid="editor-canvas-frame">
            {doc.sections.map((s, i) => (
              <div key={s.id} data-testid={`canvas-section-${s.id}`}
                draggable
                onDragStart={() => { dragId.current = s.id; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); e.stopPropagation(); const t = e.dataTransfer.getData("text/zylo-type"); if (t) addAt(t, i); else if (dragId.current) moveTo(dragId.current, s.id); dragId.current = null; }}
                onClick={() => setSel(s.id)}
                className={`relative group cursor-pointer border-b border-dashed border-black/10 transition-shadow ${sel === s.id ? "ring-2 ring-blue-500 z-10" : "hover:ring-1 hover:ring-blue-300"} ${s.hidden ? "opacity-40" : ""}`}>
                <div className="absolute top-2 right-2 z-20 hidden group-hover:flex items-center gap-0.5 bg-zinc-900 text-white rounded-full px-1.5 py-1 shadow-xl" onClick={(e) => e.stopPropagation()}>
                  <GripVertical className="h-3.5 w-3.5 opacity-50 mx-0.5" />
                  <button data-testid={`sec-up-${s.id}`} onClick={() => move(s.id, -1)} className="h-6 w-6 rounded-full hover:bg-white/15 flex items-center justify-center"><ArrowUp className="h-3 w-3" /></button>
                  <button data-testid={`sec-down-${s.id}`} onClick={() => move(s.id, 1)} className="h-6 w-6 rounded-full hover:bg-white/15 flex items-center justify-center"><ArrowDown className="h-3 w-3" /></button>
                  <button data-testid={`sec-dup-${s.id}`} onClick={() => duplicate(s.id)} className="h-6 w-6 rounded-full hover:bg-white/15 flex items-center justify-center"><Copy className="h-3 w-3" /></button>
                  <button data-testid={`sec-hide-${s.id}`} onClick={() => mutate(s.id, (x) => ({ ...x, hidden: !x.hidden }))} className="h-6 w-6 rounded-full hover:bg-white/15 flex items-center justify-center"><EyeOff className="h-3 w-3" /></button>
                  <button data-testid={`sec-del-${s.id}`} onClick={() => remove(s.id)} className="h-6 w-6 rounded-full hover:bg-red-500 flex items-center justify-center"><Trash2 className="h-3 w-3" /></button>
                </div>
                <span className="absolute top-2 left-2 z-20 hidden group-hover:block bg-zinc-900/80 text-white text-[9px] font-extrabold uppercase tracking-widest px-2 py-1 rounded-full">{META[s.type]?.label || s.type}</span>
                <EditorCanvas doc={{ design: doc.design, sections: [s] }} theme={payload.theme} store={w} info={w.business_info || {}} branding={payload.branding} products={products} />
              </div>
            ))}
            {doc.sections.length === 0 && (
              <div className="py-24 text-center text-sm text-muted-foreground">Drag elements from the left panel to start building</div>
            )}
          </div>
        </div>

        {/* RIGHT: settings */}
        <div className="hidden lg:block bg-card border-l border-border overflow-y-auto p-4" data-testid="editor-settings">
          {sec ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <p className="font-display font-extrabold">{META[sec.type]?.label || sec.type}</p>
                <button onClick={() => setSel(null)} className="text-xs font-bold text-muted-foreground hover:text-foreground">Done</button>
              </div>
              <div className="space-y-4">
                {(FIELDS[sec.type] || []).map((f) => (
                  <div key={f.k}>
                    <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">{f.l}</p>
                    {f.t === "text" && <Input data-testid={`field-${f.k}`} value={sec.props[f.k] ?? ""} onChange={(e) => setProp(sec.id, f.k, e.target.value)} className="h-10" />}
                    {f.t === "textarea" && <textarea data-testid={`field-${f.k}`} rows={4} value={sec.props[f.k] ?? ""} onChange={(e) => setProp(sec.id, f.k, e.target.value)} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus:ring-1 focus:ring-ring" />}
                    {f.t === "select" && (
                      <select data-testid={`field-${f.k}`} value={sec.props[f.k] ?? ""} onChange={(e) => setProp(sec.id, f.k, e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                        {f.opts.map((o) => { const [v, l] = Array.isArray(o) ? o : [o, o]; return <option key={v} value={v}>{l}</option>; })}
                      </select>
                    )}
                    {f.t === "align" && (
                      <div className="flex gap-1">
                        {["left", "center", "right"].map((a) => (
                          <button key={a} data-testid={`field-align-${a}`} onClick={() => setProp(sec.id, f.k, a)}
                            className={`flex-1 h-9 rounded-md border text-xs font-bold capitalize transition-colors ${sec.props[f.k] === a ? "gradient-bg text-white border-0" : "border-border hover:bg-muted"}`}>{a}</button>
                        ))}
                      </div>
                    )}
                    {f.t === "toggle" && (
                      <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                        <input type="checkbox" data-testid={`field-${f.k}`} checked={!!sec.props[f.k]} onChange={(e) => setProp(sec.id, f.k, e.target.checked)} className="h-4 w-4 accent-[var(--p2)]" />
                        {sec.props[f.k] ? "On" : "Off"}
                      </label>
                    )}
                    {(f.t === "image" || f.t === "images") && (
                      <div className="space-y-2">
                        {f.t === "image" && sec.props[f.k] && <img src={fileUrl(sec.props[f.k])} alt="" className="w-full h-24 object-cover rounded-lg border border-border" />}
                        <div className="flex gap-2">
                          <Input value={sec.props[f.k] ?? ""} onChange={(e) => setProp(sec.id, f.k, e.target.value)} placeholder={f.t === "images" ? "One URL per line" : "Image URL"} className="h-9 text-xs" data-testid={`field-${f.k}`} />
                          <label className="h-9 px-3 rounded-md border border-border text-xs font-bold flex items-center gap-1 cursor-pointer hover:bg-muted shrink-0" data-testid={`upload-${f.k}`}>
                            <Upload className="h-3 w-3" /> Upload
                            <input type="file" accept="image/*" className="hidden" onChange={(e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              if (f.t === "images") upload(file, (url) => setProp(sec.id, f.k, `${sec.props[f.k] ? sec.props[f.k] + "\n" : ""}${url}`));
                              else upload(file, (url) => setProp(sec.id, f.k, url));
                              e.target.value = "";
                            }} />
                          </label>
                        </div>
                      </div>
                    )}
                    {f.t === "cards" && <CardListEditor sec={sec} of={f.of} listKey={f.k} setProp={setProp} upload={upload} />}
                  </div>
                ))}
              </div>
              {/* Style */}
              <p className="overline-label mt-6 mb-3">Section style</p>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <label className="text-xs font-bold">Background
                    <input type="color" data-testid="style-bg" value={sec.style?.bg || "#ffffff"} onChange={(e) => setStyle(sec.id, "bg", e.target.value)} className="block w-full h-9 mt-1 rounded-md border border-border cursor-pointer" />
                  </label>
                  <label className="text-xs font-bold">Text color
                    <input type="color" data-testid="style-color" value={sec.style?.color || "#111111"} onChange={(e) => setStyle(sec.id, "color", e.target.value)} className="block w-full h-9 mt-1 rounded-md border border-border cursor-pointer" />
                  </label>
                </div>
                {(sec.style?.bg || sec.style?.color) && (
                  <button onClick={() => mutate(sec.id, (x) => ({ ...x, style: { ...x.style, bg: "", color: "" } }))} className="text-[11px] font-bold text-muted-foreground hover:text-foreground">Reset to theme colors</button>
                )}
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Vertical spacing</p>
                  <div className="flex gap-1">
                    {[["sm", "S"], ["md", "M"], ["lg", "L"]].map(([v, l]) => (
                      <button key={v} data-testid={`style-pad-${v}`} onClick={() => setStyle(sec.id, "padY", v)}
                        className={`flex-1 h-9 rounded-md border text-xs font-bold ${sec.style?.padY === v ? "gradient-bg text-white border-0" : "border-border hover:bg-muted"}`}>{l}</button>
                    ))}
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input type="checkbox" data-testid="style-hidden" checked={!!sec.hidden} onChange={(e) => mutate(sec.id, (x) => ({ ...x, hidden: e.target.checked }))} className="h-4 w-4 accent-[var(--p2)]" />
                  Hide section
                </label>
                <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                  <input type="checkbox" data-testid="style-hide-mobile" checked={!!sec.hideMobile} onChange={(e) => mutate(sec.id, (x) => ({ ...x, hideMobile: e.target.checked }))} className="h-4 w-4 accent-[var(--p2)]" />
                  Hide on mobile
                </label>
              </div>
            </div>
          ) : (
            <div>
              <p className="font-display font-extrabold mb-1">Design & Branding</p>
              <p className="text-xs text-muted-foreground mb-4">Theme stays the base — these override it. Products, orders and customers are never touched.</p>
              <div className="space-y-3">
                {COLOR_KEYS.map(([k, l]) => (
                  <label key={k} className="flex items-center gap-3 text-xs font-bold" data-testid={`design-color-${k}`}>
                    <input type="color" value={doc.design.colors?.[k] || ({ primary: "#1E3A8A", secondary: "#F1F5F9", accent: "#F59E0B", bg: "#FFFFFF", text: "#111111" }[k])}
                      onChange={(e) => setColor(k, e.target.value)} className="h-9 w-12 rounded-md border border-border cursor-pointer" />
                    <span className="flex-1">{l}</span>
                  </label>
                ))}
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Heading font</p>
                  <select data-testid="design-font" value={doc.design.font || ""} onChange={(e) => setDesign("font", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Theme default</option>
                    {HEAD_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Body font</p>
                  <select data-testid="design-body-font" value={doc.design.body_font || ""} onChange={(e) => setDesign("body_font", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Theme default</option>
                    {BODY_FONTS.map((f) => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-extrabold text-muted-foreground mb-1.5">Corner radius</p>
                  <select data-testid="design-radius" value={doc.design.radius || ""} onChange={(e) => setDesign("radius", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm">
                    <option value="">Theme default</option>
                    {["2px", "4px", "8px", "12px", "16px", "20px"].map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
              </div>
              <p className="overline-label mt-6 mb-2">Page</p>
              <div className="text-xs text-muted-foreground space-y-1.5">
                <p>Theme: <span className="font-bold text-foreground">{payload.theme?.name}</span></p>
                <p>Sections: <span className="font-bold text-foreground">{doc.sections.length}</span></p>
                <p className="pt-2 leading-relaxed">Select any section on the canvas to edit its content, images, colors and spacing.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
